(() => {
if (customElements.get('koko-essentials-scene')) return;
class KokoEssentialsScene extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.observer?.disconnect();
    this.animations?.forEach(animation => animation.cancel());
    this.events = new AbortController();
    const signal = this.events.signal;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const planes = [...this.querySelectorAll('.koko-essentials__plane')];
    this.animations = [];
    this.classList.remove('is-revealed');
    this.classList.add('is-reveal-ready');
    let revealing = false;
    let inView = false;
    const sync = () => {
      this.animations.forEach(animation => {
        if (animation.playState === 'finished' || animation.playState === 'idle') return;
        if (inView && !document.hidden) animation.play();
        else animation.pause();
      });
    };

    const reveal = async () => {
      if (revealing) return;
      revealing = true;
      await Promise.all(planes.map(plane => plane.querySelector('img').decode().catch(() => {})));
      if (signal.aborted) return;
      this.classList.add('is-revealed');
      if (motion.matches) return;
      const mobile = matchMedia('(max-width: 749px)').matches;
      const stage = this.querySelector('.koko-essentials__stage');
      const resting = planes.map(plane => {
        const transform = getComputedStyle(plane).transform;
        return transform === 'none' ? 'translate(0px, 0px)' : transform;
      });
      this.animations = planes.map((plane, index) => {
        const center = mobile ? plane.offsetTop + plane.offsetHeight / 2 : plane.offsetLeft + plane.offsetWidth / 2;
        const direction = center < (mobile ? stage.clientHeight : stage.clientWidth) / 2 ? -1 : 1;
        const distance = (mobile ? stage.clientHeight : stage.clientWidth) * 0.65;
        const offset = mobile ? '0px, ' + direction * distance + 'px' : direction * distance + 'px, 24px';
        const settle = mobile ? '0px, ' + -direction * 8 + 'px' : -direction * 10 + 'px, -8px';
        return plane.animate([
          { transform: resting[index] + ' translate(' + offset + ')', offset: 0 },
          { transform: resting[index] + ' translate(' + settle + ')', offset: 0.8 },
          { transform: resting[index], offset: 1 }
        ], { duration: 650, delay: index * 650, easing: 'cubic-bezier(0.25, 0.55, 0.3, 1)', fill: 'backwards' });
      });
      sync();
      await Promise.all(this.animations.map(animation => animation.finished.catch(() => {})));
      if (signal.aborted || motion.matches || this.resized) return;
      if (this.classList.contains('koko-peptide-scene')) {
        this.animations = planes.map((plane, index) => {
          const image = plane.querySelector('img');
          const tilt = (mobile ? [18, 16, 10] : [24, 20, 12])[index];
          const start = 0.08 + index * 0.07;
          return image.animate([
            { offset: 0, transform: 'rotate(0deg)' },
            { offset: start, transform: 'rotate(0deg)', easing: 'cubic-bezier(0.45, 0, 0.8, 0.5)' },
            { offset: start + 0.12, transform: 'rotate(' + tilt + 'deg)', easing: 'ease-out' },
            { offset: 0.40, transform: 'rotate(' + tilt + 'deg)', easing: 'cubic-bezier(0.22, 0.7, 0.3, 1)' },
            { offset: 0.57 + index * 0.025, transform: 'rotate(-3deg)', easing: 'ease-in-out' },
            { offset: 0.73, transform: 'rotate(0deg)' },
            { offset: 1, transform: 'rotate(0deg)' }
          ], { duration: 4200, iterations: Infinity });
        });
        sync();
        return;
      }
      this.animations = planes.map((plane, index) => plane.animate([
        { transform: resting[index] },
        { transform: resting[index] + ' translateY(' + (index === 0 ? -7 : 7) + 'px)' },
        { transform: resting[index] }
      ], { duration: 4400 + index * 600, iterations: Infinity, easing: 'ease-in-out' }));
      sync();
    };

    this.observer = new IntersectionObserver(entries => {
      inView = entries.some(entry => entry.isIntersecting);
      if (inView) reveal();
      sync();
    }, { threshold: 0.15 });
    this.observer.observe(this);
    motion.addEventListener('change', () => {
      if (motion.matches) {
        this.animations.forEach(animation => animation.cancel());
        reveal();
      }
    }, { signal });
    this.resized = false;
    let layoutWidth = innerWidth;
    window.addEventListener('resize', () => {
      if (innerWidth === layoutWidth) return;
      layoutWidth = innerWidth;
      this.resized = revealing;
      this.animations.forEach(animation => animation.cancel());
    }, { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    if (motion.matches) reveal();
  }

  disconnectedCallback() {
    this.events?.abort();
    this.observer?.disconnect();
    this.animations?.forEach(animation => animation.cancel());
    this.classList.remove('is-reveal-ready', 'is-revealed');
  }
}
if (!customElements.get('koko-essentials-scene')) customElements.define('koko-essentials-scene', KokoEssentialsScene);
})();
